'use client';

import {
  useController,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import {
  useMemo,
  cloneElement,
  createElement,
  isValidElement,
  type JSX,
  type ReactElement,
  type ComponentType,
} from 'react';

import { Field } from './Field';
import { Input, type IInputProps } from '../Input';

import type { AsProps } from '../interface';
import type { IBaseFormInputProps } from './interface';

type TComponent = ReactElement | ComponentType<TAny>;

interface IFormInputProps<
  T extends undefined | TComponent,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends IBaseFormInputProps<TFieldValues, TName> {
  component?: T | typeof Input;
  valueProp?: keyof AsProps<T, IInputProps>;
}

export function FormInput<
  T extends TComponent,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  rules,
  control,
  disabled,
  valueProp,
  component,
  errorText,
  helperText,
  optionalText,
  defaultValue,
  shouldUnregister,
  onChange: onExternalChange,
  ...rest
}: IFormInputProps<T, TFieldValues, TName> &
  AsProps<T, IInputProps>): JSX.Element {
  component ||= Input;
  const { field, fieldState, formState } = useController<TFieldValues, TName>({
    name,
    rules,
    control,
    disabled,
    defaultValue,
    shouldUnregister,
  });

  const { isSubmitting } = formState;
  const { error, isTouched } = fieldState;

  const content = useMemo(() => {
    const { onChange: onInternalChange, ...fieldRest } = field;
    const isValid = isValidElement(component);

    const onChange: typeof onInternalChange = (event) => {
      onInternalChange(event);
      onExternalChange?.(event);
    };

    const props: IInputProps = {
      ...rest,
      ...fieldRest,
      onChange,
    };

    if (valueProp) {
      (props as TAny)[valueProp] = fieldRest.value;
    }

    if (isValid) {
      return cloneElement(component as TAny, props);
    }

    return createElement(component as ComponentType<TAny>, props);
  }, [component, field, onExternalChange, rest, valueProp]);

  return (
    <Field
      label={label}
      helperText={helperText}
      disabled={isSubmitting}
      optionalText={optionalText}
      errorText={error?.message || errorText}
      invalid={!!error?.message && isTouched}
    >
      {content}
    </Field>
  );
}
